<template>
  <v-card v-if="!hidden || editMode" :loading="loading" class="d-flex flex-wrap">
    <component
      :is="mapComponent"
      ref="map"
      v-model:show-nearby="showNearby"
      v-model:map-filters="forcedQuery"
      v-model:picker-coordinates="pickerCoordinates"
      class="flex-grow-1"
      :hide-controls="hideControls"
      :read-only="readOnly"
      :disable-show-nearby="showNearbyDisabled"
      :assets="assets"
      :clusters="clusters"
      :main-asset="mainAsset"
      :show-main-asset-relations="showMainAssetRelations"
      :picker="picker"
      :zoom="zoom"
      :loading="loading"
      :clusters-loading="clustersLoading"
      :assets-loading="assetsLoading"
      @interact="interactMap"
    />

    <v-fade-transition>
      <v-card
        v-if="editMode"
        style="background-color: rgba(0, 0, 0, 0.5); inset: 0; z-index: 999"
        class="d-flex flex-column justify-center align-center position-absolute"
      >
        <v-card-title class="text-white" style="text-shadow: 0 0 10px black">
          {{ hidden ? $t("hidden") : $t("visible") }}
        </v-card-title>
        <v-btn
          v-if="hidden"
          prepend-icon="visibility"
          color="secondary"
          variant="flat"
          class="custom-hover-secondary-transparent"
          @click="() => emit('update:hidden', false)"
          >{{ $t("dashboard.showMap") }}</v-btn
        >
        <v-btn
          v-else
          prepend-icon="visibility_off"
          color="secondary"
          variant="flat"
          class="custom-hover-secondary-transparent"
          @click="() => emit('update:hidden', true)"
          >{{ $t("dashboard.hideMap") }}</v-btn
        >
      </v-card>
    </v-fade-transition>
  </v-card>
</template>

<script setup lang="ts">
import isEqual from "lodash-es/isEqual"

const mapRef = useTemplateRef("map")
const emit = defineEmits<{ (e: "update:hidden", value: boolean): void }>()

const pickerCoordinates = defineModel<LatLngAlt>("pickerCoordinates")

const fetchedAssets = ref<Array<GeoAsset>>([])
const clusters = ref<Array<GeoCluster>>([])
const clustersLoading = ref(false)

const assetsFetched = ref(false)
const assetsLoading = ref(false)

const {
  disableShowNearby = false,
  editMode = false,
  fetchMapData = undefined,
  forceMap = undefined,
  hidden = false,
  hideControls = false,
  loading = false,
  mainAsset = undefined,
  additionalAssets = [],
  picker = false,
  readOnly = false,
  showMainAssetRelations = false,
  zoom = undefined,
} = defineProps<{
  disableShowNearby?: boolean
  editMode?: boolean
  fetchMapData?: FetchMapData
  forceMap?: "google" | "openLayers"
  hidden?: boolean
  hideControls?: boolean
  loading?: boolean
  mainAsset?: LookupAsset
  additionalAssets?: Array<LookupAsset>,
  picker?: boolean
  readOnly?: boolean
  showMainAssetRelations?: boolean
  zoom?: number
}>()

const assets = computed<Array<MapAsset>>(() => [...additionalAssets, ...fetchedAssets.value])

const { useGoogleMaps } = useUserPreferences()
const { getGeoAssets, getGeoClusters } = useGeoAssetsStore()

const showNearby = useLocalStorage<boolean>("map:showNearby", false)
const forcedQuery = ref<Record<string, FilterQueryValue>>({})

const clusterAbortController = ref<AbortController | null>(null)
const assetAbortController = ref<AbortController | null>(null)

const mapComponent = computed(() => {
  if (forceMap) {
    return forceMap === "google" ? resolveComponent("SamGoogleMap") : resolveComponent("OpenLayersMap")
  }

  return useGoogleMaps.value ? resolveComponent("SamGoogleMap") : resolveComponent("OpenLayersMap")
})

const refetchNearbyClusters = ref(false)
const showNearbyDisabled = computed(() => disableShowNearby || clusters.value?.length !== 1)

const interactMap = async () => {
  if (assetsLoading.value || assetsFetched.value) {
    return
  }

  fetchAssets()
}

const fetchAssets = async (fetchDataOverwrite?: FetchMapData) => {
  fetchedAssets.value = []
  const fetchMapDataMethod = fetchDataOverwrite || fetchMapData

  if (!fetchMapDataMethod) {
    return
  }

  assetsLoading.value = true

  const currentRequestAbortController = new AbortController()
  assetAbortController.value = currentRequestAbortController

  const fetchedAssetsData = await getGeoAssets(fetchMapDataMethod, forcedQuery.value, !showNearbyDisabled.value && showNearby.value)

  if (currentRequestAbortController.signal.aborted) {
    return
  }

  fetchedAssets.value = fetchedAssetsData
  assetsLoading.value = false
  assetsFetched.value = true
  assetAbortController.value = null
}

const fetchClusters = async () => {
  const originalClusterLength = clusters.value?.length ?? 0

  if (!fetchMapData) {
    return
  }

  // Abort any ongoing asset fetch if they exists, since this is regarding the previous cluster
  if (assetAbortController.value) {
    assetAbortController.value.abort()
  }

  // Abort any previous cluster fetch if they exists
  if (clusterAbortController.value) {
    clusterAbortController.value.abort()
  }

  // Reset asset loading data since it resets after clusters changed
  assetsFetched.value = false
  assetsLoading.value = false

  clustersLoading.value = true

  const currentRequestAbortController = new AbortController()

  clusterAbortController.value = currentRequestAbortController

  const fetchedClusters = await getGeoClusters(
    fetchMapData,
    forcedQuery.value,
    originalClusterLength === 1 && !disableShowNearby && showNearby.value,
  )

  if (currentRequestAbortController.signal.aborted) {
    return
  }

  clusters.value = fetchedClusters

  clustersLoading.value = false
  clusterAbortController.value = null

  // Avoid infinite loop when clusters should be refetched because the correct cluster with show nearby was not fetched
  if (fetchedClusters.length === 1 && showNearby.value && originalClusterLength !== 1 && !refetchNearbyClusters.value) {
    refetchNearbyClusters.value = true

    fetchClusters()
    return
  }

  refetchNearbyClusters.value = false
}

watch(
  [() => fetchMapData, () => mainAsset, showNearby, forcedQuery],
  (newData, oldData) => {
    if (isEqual(newData, oldData)) {
      return
    }

    if (mainAsset && mainAsset.location.gps && showNearby.value) {
      fetchAssets({
        type: FetchMapType.NEARBY_FROM_CENTER,
        data: {
          center: mainAsset.location.gps.coordinates as [number, number],
          distance: 1000,
        },
      })
      return
    }

    if (!fetchMapData) {
      clusters.value = []
      fetchedAssets.value = []
      return
    }

    fetchClusters()
  },
  { deep: true, immediate: true },
)

defineExpose({ map: mapRef })
</script>
